/**
 * Resolución de URLs de instancia según renderMode y contrato manifest.
 * Complementa url/path/key legacy + dicomUrl/previewUrl.
 * No importar renderMode.js (evita ciclo).
 */

export function encodeObjectStoragePath(relPath) {
  const cleaned = String(relPath || '')
    .replace(/\\/g, '/')
    .replace(/^\/+/, '')
  return cleaned
    .split('/')
    .filter((seg) => seg.length > 0)
    .map((seg) => {
      try {
        return encodeURIComponent(decodeURIComponent(seg))
      } catch (e) {
        return encodeURIComponent(seg)
      }
    })
    .join('/')
}

function resolveLegacyAssetUrl(inst, assetBaseUrl) {
  if (!inst || typeof inst !== 'object') return null

  const rawUrl = inst.url != null ? String(inst.url).trim() : ''
  if (/^https?:\/\//i.test(rawUrl)) {
    return rawUrl
  }

  const rel =
    (inst.path != null && String(inst.path).trim()) ||
    (inst.key != null && String(inst.key).trim()) ||
    (rawUrl && !/^https?:\/\//i.test(rawUrl) ? rawUrl : '') ||
    ''

  if (!rel) return null
  if (/^https?:\/\//i.test(rel)) return rel

  if (!assetBaseUrl) return null
  const baseNorm = String(assetBaseUrl).endsWith('/')
    ? String(assetBaseUrl)
    : `${assetBaseUrl}/`
  return `${baseNorm}${encodeObjectStoragePath(rel)}`
}

/**
 * URL absoluta del binario DICOM (dicomUrl o legacy).
 * RANGE_ZIP sin dicomUrl https → null (loader usa archiveUrl+offsets).
 * orthanc-internal: nunca unir a assetBaseUrl.
 * @param {object} inst
 * @param {string|null} assetBaseUrl
 * @returns {string|null}
 */
export function resolveDiagnosticUrl(inst, assetBaseUrl) {
  if (!inst || typeof inst !== 'object') return null
  if (inst.dicomFetchType === 'RANGE_ZIP') {
    const direct = inst.dicomUrl != null ? String(inst.dicomUrl).trim() : ''
    if (/^https?:\/\//i.test(direct)) return direct
    return null
  }
  const direct = inst.dicomUrl != null ? String(inst.dicomUrl).trim() : ''
  if (/^https?:\/\//i.test(direct)) return direct
  if (direct && assetBaseUrl) {
    if (/^orthanc-internal\//i.test(direct.replace(/^\/+/, ''))) {
      return null
    }
    const baseNorm = String(assetBaseUrl).endsWith('/')
      ? String(assetBaseUrl)
      : `${assetBaseUrl}/`
    return `${baseNorm}${encodeObjectStoragePath(direct)}`
  }
  // Clave path/key no debe filtrar orthanc-internal como asset público
  const rel =
    (inst.path != null && String(inst.path).trim()) ||
    (inst.key != null && String(inst.key).trim()) ||
    ''
  if (rel && /^orthanc-internal\//i.test(rel.replace(/^\/+/, ''))) {
    return null
  }
  return resolveLegacyAssetUrl(inst, assetBaseUrl)
}

/**
 * @param {object} inst
 * @param {string|null} [assetBaseUrl]
 * @returns {string|null}
 */
export function resolvePreviewUrl(inst, assetBaseUrl) {
  if (!inst || typeof inst !== 'object') return null
  const candidates = [inst.previewUrl, inst.webpKey]
  for (let i = 0; i < candidates.length; i++) {
    const u = candidates[i]
    if (u == null) continue
    const s = String(u).trim()
    if (!s) continue
    if (/^https?:\/\//i.test(s)) return s
    // Ruta relativa + base del CDN
    if (assetBaseUrl) {
      const baseNorm = String(assetBaseUrl).endsWith('/')
        ? String(assetBaseUrl)
        : `${assetBaseUrl}/`
      return `${baseNorm}${encodeObjectStoragePath(s)}`
    }
    // Sin base: solo devolver si ya era usable (path relativo no sirve al browser)
  }
  return null
}

/**
 * ¿Instancia debe ir por zip Range? DIRECT_URL / role=report / storage=loose → no.
 * @param {object|null|undefined} inst
 * @returns {boolean}
 */
function instanceWantsRangeZip(inst) {
  if (!inst || typeof inst !== 'object') return false
  if (inst.dicomFetchType === 'DIRECT_URL') return false
  const storage =
    (inst.storage != null && String(inst.storage).trim().toLowerCase()) || ''
  const role =
    (inst.role != null && String(inst.role).trim().toLowerCase()) || ''
  if (storage === 'loose' || role === 'report') return false
  return (
    inst.dicomFetchType === 'RANGE_ZIP' &&
    !!inst.archiveUrl &&
    inst.byteOffset != null &&
    inst.byteLength != null &&
    Number(inst.byteLength) > 0
  )
}

/**
 * @param {object} inst
 * @param {'DIAGNOSTIC'|'PREVIEW'} renderMode
 * @param {string|null} assetBaseUrl
 * @returns {{ kind: 'preview'|'dicom', url: string|null, usedFallback: boolean }}
 */
export function resolveLoadUrl(inst, renderMode, assetBaseUrl) {
  const hasRangeZip = instanceWantsRangeZip(inst)

  // DIAGNOSTIC: DICOM (DIRECT url o RANGE_ZIP)
  if (renderMode !== 'PREVIEW') {
    if (hasRangeZip) {
      return {
        kind: 'dicom',
        url: null,
        usedFallback: false,
        dicomFetchType: 'RANGE_ZIP',
      }
    }
    const d = resolveDiagnosticUrl(inst, assetBaseUrl)
    return {
      kind: 'dicom',
      url: d,
      usedFallback: false,
      dicomFetchType: 'DIRECT_URL',
    }
  }
  const p = resolvePreviewUrl(inst, assetBaseUrl)
  if (p) {
    return { kind: 'preview', url: p, usedFallback: false }
  }
  if (hasRangeZip) {
    return {
      kind: 'dicom',
      url: null,
      usedFallback: true,
      dicomFetchType: 'RANGE_ZIP',
    }
  }
  const d = resolveDiagnosticUrl(inst, assetBaseUrl)
  return {
    kind: 'dicom',
    url: d,
    usedFallback: true,
    dicomFetchType: 'DIRECT_URL',
  }
}

/**
 * Normaliza manifest plano del ticket a shape anidado series[].
 * @param {object} raw
 * @returns {object}
 */
export function normalizeManifestShape(raw) {
  if (!raw || typeof raw !== 'object') return raw

  if (Array.isArray(raw.series) && raw.series.length > 0) {
    const series = raw.series.map((s) => {
      if (!s || !Array.isArray(s.instances)) return s
      return {
        ...s,
        instances: s.instances.map((inst) => {
          if (!inst) return inst
          if (!inst.url && inst.dicomUrl) {
            return { ...inst, url: inst.dicomUrl }
          }
          return inst
        }),
      }
    })
    return { ...raw, series }
  }

  if (Array.isArray(raw.instances) && raw.instances.length > 0) {
    const studyUid = raw.studyInstanceUID || ''
    const bySeries = new Map()
    for (const inst of raw.instances) {
      if (!inst) continue
      const suid =
        inst.seriesInstanceUID ||
        inst.SeriesInstanceUID ||
        inst.seriesUID ||
        'unknown'
      if (!bySeries.has(suid)) {
        bySeries.set(suid, {
          seriesInstanceUID: suid,
          seriesNumber: bySeries.size + 1,
          seriesDescription: '',
          instances: [],
        })
      }
      const row = bySeries.get(suid)
      const normalized = { ...inst }
      if (!normalized.url && normalized.dicomUrl) {
        normalized.url = normalized.dicomUrl
      }
      if (!normalized.studyInstanceUID && studyUid) {
        normalized.studyInstanceUID = studyUid
      }
      row.instances.push(normalized)
    }
    return {
      ...raw,
      studyInstanceUID: studyUid,
      patientId: raw.patientId,
      patientName: raw.patientName || raw.patientId || 'Unknown Patient',
      series: [...bySeries.values()],
    }
  }

  return raw
}

/**
 * Capacidades de media del manifest (preview vs dcm).
 * @param {object} rawManifest
 * @param {string|null} [assetBaseUrl]
 * @param {string|null} [archiveUrlOverride] downloadUrl firmada (ticket)
 * @returns {{ hasPreview: boolean, hasDicom: boolean, previewCount: number, dicomCount: number, total: number }}
 */
export function inspectManifestMedia(rawManifest, assetBaseUrl, archiveUrlOverride) {
  // Canonicalize para contar preview/range del formato base o firmado
  let manifest = rawManifest
  try {
    // require evita ciclo estático: manifestNormalize importa este módulo
    // eslint-disable-next-line global-require
    const { canonicalizeManifest } = require('./manifestNormalize')
    manifest = canonicalizeManifest(rawManifest, {
      assetBaseUrl: assetBaseUrl || null,
      archiveUrlOverride:
        archiveUrlOverride != null && String(archiveUrlOverride).trim()
          ? String(archiveUrlOverride).trim()
          : null,
    })
  } catch (e) {
    manifest = normalizeManifestShape(rawManifest)
  }
  let previewCount = 0
  let dicomCount = 0
  let total = 0
  if (!manifest || !Array.isArray(manifest.series)) {
    return {
      hasPreview: false,
      hasDicom: false,
      previewCount: 0,
      dicomCount: 0,
      total: 0,
    }
  }
  for (const series of manifest.series) {
    const instances = (series && series.instances) || []
    for (const inst of instances) {
      if (!inst) continue
      total += 1
      if (
        resolvePreviewUrl(inst, assetBaseUrl || null) ||
        (inst.previewUrl && String(inst.previewUrl).trim())
      ) {
        previewCount += 1
      }
      const hasRange =
        inst.dicomFetchType === 'RANGE_ZIP' &&
        inst.archiveUrl &&
        inst.byteOffset != null &&
        inst.byteLength != null &&
        Number(inst.byteLength) > 0
      if (hasRange || resolveDiagnosticUrl(inst, assetBaseUrl || null)) {
        dicomCount += 1
      }
    }
  }
  return {
    hasPreview: previewCount > 0,
    hasDicom: dicomCount > 0,
    previewCount,
    dicomCount,
    total,
  }
}
